import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useRef, useState } from "react";
import "./PreviousEmployment.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError
} from "./onboardingFeatureAccess";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../components/ui/popover";

const INITIAL_ENTRY = {
  prevEmpRecordId: null, // backend's primary key (previous_emp_info_Id) for update endpoint
  employerName: "",
  referenceEmpName: "",
  dateOfJoin: "",
  dateOfRelease: "",
  isCompleted: true,
  relievingLetterFile: null,
  experienceLetterFile: null,
  supportingDocumentFile: null
};

const FILE_FIELDS = [
{ key: "relievingLetterFile", titleKey: "onboarding.labels.relievingLetter", hintKey: "onboarding.options.fileHint.pdfJpg" },
{ key: "experienceLetterFile", titleKey: "onboarding.labels.experienceLetter", hintKey: "onboarding.options.fileHint.pdfJpg" },
{ key: "supportingDocumentFile", titleKey: "onboarding.labels.supportingDocument", hintKey: "onboarding.options.fileHint.pdfJpg" }];

const SAVE_ACTION = {
  title: "Save Previous Employment",
  serviceCode: "HR10A",
  endpoint: "/saas/hrms/emp-previous-employment/add",
  method: "POST"
};

function isValidIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function apiDateToStorageDate(value) {
  if (!value) return "";
  let str = String(value).trim();
  if (str.includes("T")) str = str.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  return "";
}

function displayDateToApiDate(value) {
  const str = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  return undefined;
}

function readFileAsBase64(file) {
  if (!file) return Promise.resolve(undefined);
  if (file instanceof Blob || file instanceof File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        resolve(result.includes(",") ? result.split(",")[1] : result);
      };
      reader.onerror = () => reject(new Error(i18n.t("onboarding.errors.unableToReadFile")));
      reader.readAsDataURL(file);
    });
  }
  if (typeof file === "object" && file.base64) {
    return Promise.resolve(file.base64);
  }
  return Promise.resolve(undefined);
}

export default function PreviousEmployment({ accessToken, tenantCode, initialEmployeeId = "", breadcrumb, onBack, onNext, onSuccess, isTabMode = false, isActive = true, saveTriggerRef }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [entries, setEntries] = useState([{ ...INITIAL_ENTRY }]);
  const [employeeId, setEmployeeId] = useState(String(initialEmployeeId || ""));
  // fileInputRefs[entryIndex][fieldKey] → ref to the hidden <input type="file">
  const fileInputRefs = useRef({});
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [entryErrors, setEntryErrors] = useState([{}]);
  const [entryTouched, setEntryTouched] = useState([{}]);
  const [employeeIdError, setEmployeeIdError] = useState("");

  const tenantId = sessionDB.getTenantId();

  function apiDateToDisplayDate(value) {
    return apiDateToStorageDate(value);
  }

  const fetchPrevious = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const headers = {
        'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
        'x-tenant-id': String(tenantId || sessionDB.getTenantId() || ""),
        'x-service-code': 'HR10S',
        authorization: `Bearer ${accessToken || sessionDB.getAccessToken() || ""}`,
        'Content-Type': 'application/json'
      };

      // CORRECT ENDPOINT: GET /saas/hrms/emp-previous-employment/search/emp/{empId}
      // The old endpoint /search/{empId} returned 404 because "/emp/" segment was missing
      const response = await fetch(
        resolveApiUrl(`/saas/hrms/emp-previous-employment/search/emp/${Number(initialEmployeeId)}`),
        { method: 'GET', headers }
      );

      if (response.ok) {
        const body = await response.json();
        const candidates = [
        body?.data?.content,
        body?.data?.records,
        body?.data?.previousEmployment,
        body?.data?.items,
        body?.data?.data?.content,
        body?.data,
        body?.content,
        body?.records,
        body];

        const arrayCandidate = candidates.find((c) => Array.isArray(c));
        const list = arrayCandidate ? arrayCandidate : body?.data && typeof body.data === "object" ? [body.data] : [body];

        const validList = list.filter((item) => item && (
        item.employerName || item.companyName || item.employer || item.previousEmployer ||
        item.organizationName || item.company || item.lastCompany ||
        item.previousEmpInfoId || item.prevEmpInfoId || item.previous_emp_info_Id ||
        item.previous_emp_info_id || item.previousEmpInfo_Id || item.id)
        );

        if (validList.length > 0) {
          const hasValidBase64 = (str) => typeof str === "string" && str.trim().length > 20 && str.trim() !== "null" && str.trim() !== "undefined";
          const hasTruthyFlag = (...values) =>
          values.some((v) => v === true || v === "true" || v === 1 || v === "Y" || v === "y");
          const firstName = (...values) => {
            for (const value of values) {
              if (typeof value === "string" && value.trim() && value.trim() !== "null" && value.trim() !== "undefined") {
                return value.trim();
              }
            }
            return null;
          };
          const resolveRecordId = (item) =>
          item.previousEmpInfoId ??
          item.prevEmpInfoId ??
          item.previous_emp_info_Id ??
          item.previous_emp_info_id ??
          item.previousEmpInfo_Id ??
          item.prev_emp_info_id ??
          item.prevEmpRecordId ??
          item.id ??
          null;

          const restorePrevFile = ({ base64Candidates, nameCandidates, flagCandidates, defaultName, docType, recordId }) => {
            const base64 = base64Candidates.find(hasValidBase64);
            const fileName = firstName(...nameCandidates);
            const flagged = hasTruthyFlag(...flagCandidates);
            // recordId alone is not enough — only restore when content/metadata says a file exists
            if (!base64 && !fileName && !flagged) return null;
            return {
              name: fileName || defaultName,
              base64: base64 || undefined,
              isExisting: true,
              docType,
              prevEmpRecordId: recordId
            };
          };

          const mappedEntries = validList.map((item) => {
            const recordId = resolveRecordId(item);
            const relievingB64 = [item.relievingLetterBase64, item.relievingLetterDoc, item.relievingLetter, item.relievingLetterFile].find(hasValidBase64);
            const experienceB64 = [item.experienceLetterBase64, item.experienceLetterDoc, item.experienceLetter, item.experienceLetterFile].find(hasValidBase64);
            const supportingB64 = [item.supportingDocumentBase64, item.supportingDocumentDoc, item.supportingDocument, item.supportingDocumentFile].find(hasValidBase64);

            const relievingName = firstName(
              item.relievingLetterFileName,
              item.relievingLetterName,
              typeof item.relievingLetterFile === "string" ? item.relievingLetterFile : null
            );
            const experienceName = firstName(
              item.experienceLetterFileName,
              item.experienceLetterName,
              typeof item.experienceLetterFile === "string" ? item.experienceLetterFile : null
            );
            const supportingName = firstName(
              item.supportingDocumentFileName,
              item.supportingDocumentName,
              typeof item.supportingDocumentFile === "string" ? item.supportingDocumentFile : null
            );

            return {
              prevEmpRecordId: recordId,
              employerName: item.employerName || item.companyName || item.employer || item.previousEmployer || item.organizationName || item.company || item.lastCompany || "",
              referenceEmpName: item.referenceEmpName || item.referenceName || item.referenceEmp || item.referencePerson || "",
              dateOfJoin: apiDateToDisplayDate(item.dateOfJoin || item.dateOfJoining || item.joiningDate || item.startDate || item.fromDate),
              dateOfRelease: apiDateToDisplayDate(item.dateOfRelease || item.dateOfRelieving || item.relievingDate || item.releaseDate || item.endDate || item.toDate),
              isCompleted: true,
              relievingLetterFile: restorePrevFile({
                base64Candidates: [item.relievingLetterBase64, item.relievingLetterDoc, item.relievingLetter, item.relievingLetterFile, relievingB64],
                nameCandidates: [relievingName],
                flagCandidates: [item.hasRelievingLetter, item.relievingLetterAvailable, item.relievingLetterUploaded],
                defaultName: "Relieving_Letter.pdf",
                docType: "relievingletter",
                recordId
              }),
              experienceLetterFile: restorePrevFile({
                base64Candidates: [item.experienceLetterBase64, item.experienceLetterDoc, item.experienceLetter, item.experienceLetterFile, experienceB64],
                nameCandidates: [experienceName],
                flagCandidates: [item.hasExperienceLetter, item.experienceLetterAvailable, item.experienceLetterUploaded],
                defaultName: "Experience_Letter.pdf",
                docType: "experienceletter",
                recordId
              }),
              supportingDocumentFile: restorePrevFile({
                base64Candidates: [item.supportingDocumentBase64, item.supportingDocumentDoc, item.supportingDocument, item.supportingDocumentFile, supportingB64],
                nameCandidates: [supportingName],
                flagCandidates: [item.hasSupportingDocument, item.supportingDocumentAvailable, item.supportingDocumentUploaded],
                defaultName: "Supporting_Document.pdf",
                docType: "supportingdocument",
                recordId
              })
            };
          });

          // Probe download endpoints when search omitted file metadata
          const empIdVal = Number(initialEmployeeId);
          const probeHeaders = {
            "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
            "x-tenant-id": String(tenantId || sessionDB.getTenantId() || ""),
            "x-service-code": "HR10D",
            authorization: `Bearer ${accessToken || sessionDB.getAccessToken() || ""}`
          };
          const fileProbes = [
          ["relievingLetterFile", "relievingletter", "Relieving_Letter.pdf"],
          ["experienceLetterFile", "experienceletter", "Experience_Letter.pdf"],
          ["supportingDocumentFile", "supportingdocument", "Supporting_Document.pdf"]];


          const hydratedEntries = await Promise.all(mappedEntries.map(async (entry) => {
            const recordId = entry.prevEmpRecordId;
            if (!recordId) return entry;
            const updated = { ...entry };
            await Promise.all(fileProbes.map(async ([fieldKey, docType, defaultName]) => {
              if (updated[fieldKey]) return;
              try {
                const downloadUrl = resolveApiUrl(
                  `/saas/hrms/emp-previous-employment/download/${empIdVal}/${Number(recordId)}/${docType}`
                );
                const res = await fetch(downloadUrl, { method: "GET", headers: probeHeaders });
                if (!res.ok) return;
                await res.blob();
                updated[fieldKey] = {
                  name: defaultName,
                  isExisting: true,
                  docType,
                  prevEmpRecordId: recordId
                };
              } catch {

                // file not present
              }}));
            return updated;
          }));

          setEntries(hydratedEntries);
          setEntryErrors(hydratedEntries.map(() => ({})));
          setEntryTouched(hydratedEntries.map(() => ({})));
          setIsUpdate(true);
        }
      }
    } catch (err) {
      console.error("Failed to load previous employment records", err);
    }
  }, [initialEmployeeId, tenantCode, tenantId, accessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setEmployeeId("");
      setEntries([
      {
        prevEmpRecordId: null,
        employerName: "",
        referenceEmpName: "",
        dateOfJoin: "",
        dateOfRelease: "",
        isCompleted: false,
        relievingLetterFile: null,
        experienceLetterFile: null,
        supportingDocumentFile: null
      }]
      );
      setIsUpdate(false);
      setEntryErrors([{}]);
      setEntryTouched([{}]);
      setEmployeeIdError("");
      return;
    }
    setEmployeeId(String(initialEmployeeId));
    fetchPrevious();
  }, [isActive, initialEmployeeId, fetchPrevious]);

  function updateEntry(index, key, value) {
    setEntries((prev) => {
      const updated = prev.map((entry, idx) => idx === index ? { ...entry, [key]: value } : entry);
      if (entryTouched[index]?.[key]) {
        setEntryErrors((prevErrors) => {
          const next = [...prevErrors];
          next[index] = validateEntry(updated[index]);
          return next;
        });
      }
      return updated;
    });
  }

  function touchEntryField(index, key, valueOverride) {
    setEntryTouched((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [key]: true };
      return next;
    });
    const entry = valueOverride !== undefined ?
      { ...entries[index], [key]: valueOverride } :
      entries[index];
    setEntryErrors((prev) => {
      const next = [...prev];
      next[index] = validateEntry(entry);
      return next;
    });
  }

  function validateEntry(entry) {
    const errors = {};
    if (!entry.employerName.trim()) {
      errors.employerName = i18n.t("onboarding.errors.employerNameRequired");
    }
    if (!entry.referenceEmpName.trim()) {
      errors.referenceEmpName = i18n.t("onboarding.errors.referenceEmployeeNameRequired");
    }
    if (!entry.dateOfJoin.trim() || !displayDateToApiDate(entry.dateOfJoin)) {
      errors.dateOfJoin = i18n.t("onboarding.errors.joiningDateRequired");
    }
    if (!entry.dateOfRelease.trim() || !displayDateToApiDate(entry.dateOfRelease)) {
      errors.dateOfRelease = i18n.t("onboarding.errors.releaseDateRequired");
    } else if (
      isValidIsoDate(entry.dateOfJoin) &&
      isValidIsoDate(entry.dateOfRelease) &&
      entry.dateOfRelease < entry.dateOfJoin
    ) {
      errors.dateOfRelease = i18n.t("onboarding.errors.releaseBeforeJoining");
    }
    return errors;
  }

  function handleFileChange(index, key, file) {
    updateEntry(index, key, file || null);
  }

  function addAnotherEmployment() {
    setEntries((prev) => [...prev, { ...INITIAL_ENTRY }]);
    setEntryErrors((prev) => [...prev, {}]);
    setEntryTouched((prev) => [...prev, {}]);
  }

  function validateEntries() {
    const empIdError = !String(employeeId).trim() || !/^\d+$/.test(String(employeeId).trim()) ?
      i18n.t("onboarding.errors.validEmployeeIdRequired") :
      "";
    setEmployeeIdError(empIdError);

    const nextErrors = entries.map((entry) => validateEntry(entry));
    const nextTouched = entries.map(() => ({
      employerName: true,
      referenceEmpName: true,
      dateOfJoin: true,
      dateOfRelease: true
    }));

    setEntryErrors(nextErrors);
    setEntryTouched(nextTouched);

    return !empIdError && nextErrors.every((entryError) => Object.keys(entryError).length === 0);
  }

  async function handleSave() {
    setBanner(null);
    if (!validateEntries()) return;

    setLoading(true);
    try {
      for (const entry of entries) {
        const isExistingRecord = isUpdate && entry.prevEmpRecordId != null;

        const payload = {
          empId: Number(String(employeeId).trim()),
          employerName: entry.employerName.trim(),
          companyName: entry.employerName.trim(),
          referenceEmpName: entry.referenceEmpName.trim() || undefined,
          referenceName: entry.referenceEmpName.trim() || undefined,
          dateOfJoin: displayDateToApiDate(entry.dateOfJoin),
          dateOfJoining: displayDateToApiDate(entry.dateOfJoin),
          dateOfRelease: displayDateToApiDate(entry.dateOfRelease),
          dateOfRelieving: displayDateToApiDate(entry.dateOfRelease),
          relievingLetterBase64: entry.relievingLetterFile ? await readFileAsBase64(entry.relievingLetterFile) : undefined,
          experienceLetterBase64: entry.experienceLetterFile ? await readFileAsBase64(entry.experienceLetterFile) : undefined,
          supportingDocumentBase64: entry.supportingDocumentFile ? await readFileAsBase64(entry.supportingDocumentFile) : undefined
        };

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === "") delete payload[key];
        });

        // CORRECT UPDATE ENDPOINT: PUT /saas/hrms/emp-previous-employment/update/{previous_emp_info_Id}
        // Must pass the record's own ID (prevEmpRecordId), NOT the empId
        // For new entries (no prevEmpRecordId) always use the Add endpoint
        const nextEndpoint = isExistingRecord ?
        `/saas/hrms/emp-previous-employment/update/${entry.prevEmpRecordId}` :
        SAVE_ACTION.endpoint;
        const nextMethod = isExistingRecord ? "PUT" : SAVE_ACTION.method;
        const nextServiceCode = isExistingRecord ? "HR10U" : SAVE_ACTION.serviceCode;

        const response = await fetch(resolveApiUrl(nextEndpoint), {
          method: nextMethod,
          headers: {
            "Content-Type": "application/json",
            "x-tenant-code": tenantCode || "",
            "x-service-code": nextServiceCode,
            authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response);
          handleOnboardingApiError(errorBody, errorMessage, { setBanner });
          return;
        }
      }

      setBanner({ type: "success", msg: i18n.t("onboarding.messages.previousEmploymentSavedSuccessfully") });
      await fetchPrevious();
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setBanner });
    } finally {
      setLoading(false);
    }
  }

  async function openPreviousFile(fileObj) {
    if (!fileObj) return;
    const hasValidBase64 = (str) => typeof str === "string" && str.trim().length > 20 && str.trim() !== "null" && str.trim() !== "undefined";

    // 1. Direct base64 content
    if (hasValidBase64(fileObj.base64)) {
      try {
        const mimeType = fileObj.name?.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream";
        const cleanB64 = fileObj.base64.includes(",") ? fileObj.base64.split(",")[1] : fileObj.base64;
        const byteChars = atob(cleanB64.trim());
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        const blob = new Blob([bytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch {
        const cleanB64 = fileObj.base64.includes(",") ? fileObj.base64.split(",")[1] : fileObj.base64;
        window.open(`data:application/pdf;base64,${cleanB64.trim()}`, "_blank", "noopener");
      }
      return;
    }

    // 2. Newly chosen local File
    if (fileObj instanceof File) {
      const url = URL.createObjectURL(fileObj);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      return;
    }

    // 3. Fallback: Fetch document from backend download endpoint
    const recordId = fileObj.prevEmpRecordId;
    const docType = fileObj.docType;
    const empIdVal = employeeId || initialEmployeeId;
    if (recordId && docType && empIdVal) {
      try {
        const downloadUrl = resolveApiUrl(
          `/saas/hrms/emp-previous-employment/download/${Number(empIdVal)}/${Number(recordId)}/${docType}`
        );
        const res = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
            "x-tenant-id": String(tenantId || sessionDB.getTenantId() || ""),
            "x-service-code": "HR10D",
            authorization: `Bearer ${accessToken || sessionDB.getAccessToken() || ""}`
          }
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank", "noopener");
          setTimeout(() => URL.revokeObjectURL(url), 10000);
          return;
        }
      } catch (err) {
        console.error("Failed to fetch previous employment document", err);
      }
    }
  }

  function triggerFileInput(index, fieldKey) {
    const ref = fileInputRefs.current[`${index}-${fieldKey}`];
    if (ref) ref.click();
  }

  return (
    <div className={`per-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="per-main">
        {!isTabMode && breadcrumb &&
        <div className="per-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="per-bc-sep">/</span>}
                <button
              className={`per-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="per-page-header">
            <h1 className="per-page-title"><Trans i18nKey="pagination.previousEmployment" /></h1>
            <p className="per-page-sub"><Trans i18nKey="onboarding.capturePriorEmploymentRecordsAndSupportingDocuments" /></p>
          </div>
        }

        {entries.map((entry, index) =>
        <section key={index} className="per-entry-card">
            <div className="per-entry-title"><Trans i18nKey="onboarding.employment" />{index + 1}</div>
            <div className="per-grid">
              {index === 0 ?
            <div className="per-field">
                  <label className="per-label"><Trans i18nKey="common.employeeId" /></label>
                  <input className={`per-input${employeeIdError ? " error" : ""}`} placeholder={i18n.t("common.employeeId")} value={employeeId} onChange={(event) => {
                    setEmployeeId(event.target.value);
                    if (employeeIdError) setEmployeeIdError("");
                  }} readOnly={Boolean(initialEmployeeId)} />
                  <p className="per-error">{employeeIdError || ""}</p>
                </div> :
            null}

              <div className="per-field">
                <label className="per-label required"><Trans i18nKey="onboarding.employerName" /></label>
                <input className={`per-input${entryTouched[index]?.employerName && entryErrors[index]?.employerName ? " error" : ""}`} placeholder={i18n.t("onboarding.companyPvtLtd")} value={entry.employerName} onChange={(event) => updateEntry(index, "employerName", event.target.value)} onBlur={() => touchEntryField(index, "employerName")} />
                <p className="per-error">{entryTouched[index]?.employerName ? entryErrors[index]?.employerName || "" : ""}</p>
              </div>
              <div className="per-field">
                <label className="per-label required"><Trans i18nKey="onboarding.referenceName" /></label>
                <input className={`per-input${entryTouched[index]?.referenceEmpName && entryErrors[index]?.referenceEmpName ? " error" : ""}`} placeholder={i18n.t("onboarding.referenceContact")} value={entry.referenceEmpName} onChange={(event) => updateEntry(index, "referenceEmpName", event.target.value)} onBlur={() => touchEntryField(index, "referenceEmpName")} />
                <p className="per-error">{entryTouched[index]?.referenceEmpName ? entryErrors[index]?.referenceEmpName || "" : ""}</p>
              </div>
              <div className="per-field">
                <label className="per-label required"><Trans i18nKey="dateTime.joiningDate" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`per-input${entryTouched[index]?.dateOfJoin && entryErrors[index]?.dateOfJoin ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {entry.dateOfJoin && isValidIsoDate(entry.dateOfJoin) ?
                        format(parse(entry.dateOfJoin, "yyyy-MM-dd", new Date()), "PPP") :
                        <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1980, 0)}
                      endMonth={new Date()}
                      selected={isValidIsoDate(entry.dateOfJoin) ? parse(entry.dateOfJoin, "yyyy-MM-dd", new Date()) : undefined}
                      disabled={{ after: new Date() }}
                      onSelect={(date) => {
                        if (!date) return;
                        const value = format(date, "yyyy-MM-dd");
                        updateEntry(index, "dateOfJoin", value);
                        touchEntryField(index, "dateOfJoin", value);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <p className="per-error">{entryTouched[index]?.dateOfJoin ? entryErrors[index]?.dateOfJoin || "" : ""}</p>
              </div>
              <div className="per-field">
                <label className="per-label required"><Trans i18nKey="dateTime.releaseDate" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`per-input${entryTouched[index]?.dateOfRelease && entryErrors[index]?.dateOfRelease ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {entry.dateOfRelease && isValidIsoDate(entry.dateOfRelease) ?
                        format(parse(entry.dateOfRelease, "yyyy-MM-dd", new Date()), "PPP") :
                        <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1980, 0)}
                      endMonth={new Date()}
                      selected={isValidIsoDate(entry.dateOfRelease) ? parse(entry.dateOfRelease, "yyyy-MM-dd", new Date()) : undefined}
                      disabled={{
                        after: new Date(),
                        ...(isValidIsoDate(entry.dateOfJoin) ? { before: parse(entry.dateOfJoin, "yyyy-MM-dd", new Date()) } : {})
                      }}
                      onSelect={(date) => {
                        if (!date) return;
                        const value = format(date, "yyyy-MM-dd");
                        updateEntry(index, "dateOfRelease", value);
                        touchEntryField(index, "dateOfRelease", value);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <p className="per-error">{entryTouched[index]?.dateOfRelease ? entryErrors[index]?.dateOfRelease || "" : ""}</p>
              </div>
            </div>

            <div className="per-file-grid">
              {FILE_FIELDS.map((field) =>
            <div key={field.key} className="per-file-card">
                  {/* Hidden file input — triggered only via Upload button, NOT overlaid on the card */}
                  <input
                ref={(el) => {fileInputRefs.current[`${index}-${field.key}`] = el;}}
                type="file"
                accept=".pdf,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(event) => handleFileChange(index, field.key, event.target.files?.[0])} />
              
                  <div className="per-file-title"><Trans i18nKey={field.titleKey} /></div>
                  <div className="per-file-hint"><Trans i18nKey={field.hintKey} /></div>
                  {entry[field.key] ?
              <>
                      <p className="per-file-name" style={{ marginTop: "4px" }}>
                        {entry[field.key]?.name || i18n.t("onboarding.attachedFile")}
                      </p>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
                        <button
                    type="button"
                    className="per-upload-btn"
                    onClick={() => openPreviousFile(entry[field.key])}>
                          <Trans i18nKey="formActions.viewDownload" />
                        
                  </button>
                        <button
                    type="button"
                    className="per-upload-btn"
                    style={{ color: "#dc2626", borderColor: "rgba(220,38,38,0.25)" }}
                    onClick={() => handleFileChange(index, field.key, null)}>
                          <Trans i18nKey="formActions.remove" />
                        
                  </button>
                        <button
                    type="button"
                    className="per-upload-btn"
                    onClick={() => triggerFileInput(index, field.key)}>
                          <Trans i18nKey="formActions.replace" />
                        
                  </button>
                      </div>
                    </> :

              <button
                type="button"
                className="per-upload-btn"
                style={{ marginTop: "10px" }}
                onClick={() => triggerFileInput(index, field.key)}>
                      <Trans i18nKey="formActions.upload" />
                    
              </button>
              }
                </div>
            )}
            </div>
          </section>
        )}

        <button type="button" className="per-add-more" onClick={addAnotherEmployment}>
          <Trans i18nKey="formActions.addAnotherEmployment" />
        </button>

        {banner ? <div className={`per-banner ${banner.type}`}>{banner.msg}</div> : null}

        {!isTabMode && (onBack || onNext) &&
        <div className="per-actions">
            {onBack ? <button className="per-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button> : <span />}
            <button className="per-primary-btn" type="button" onClick={handleSave} disabled={loading}>
              {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
            </button>
            {onNext ? <button className="per-secondary-btn" type="button" onClick={onNext}><Trans i18nKey="pagination.next2" /></button> : null}
          </div>
        }
      </main>
    </div>);

}
